import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { actsApi } from '../../services/api';
import { usePaginatedApi } from '../../hooks/useApi';
import { LoadingState } from '../loading/LoadingState';
import { ErrorBoundary } from '../errors/ErrorBoundary';

interface ActCard {
  id: string;
  title: string;
  description: string;
  officialId: string;
  officialName?: string;
  shittyVotes: number;
  totalVotes: number;
  category: string;
  timestamp: number;
}

interface ActsListProps {
  officialId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  pageSize?: number;
  showOfficial?: boolean;
}

const ActsList: React.FC<ActsListProps> = ({
  officialId,
  sortBy = 'timestamp',
  sortOrder = 'desc',
  pageSize = 20,
  showOfficial = false,
}) => {
  const navigate = useNavigate();
  const [currentSort, setCurrentSort] = useState(sortBy);
  const [currentOrder, setCurrentOrder] = useState<'asc' | 'desc'>(sortOrder);

  const {
    data: acts,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    execute: refreshList,
    loadMore,
  } = usePaginatedApi(
    (params, cancelToken) =>
      actsApi.list(
        {
          officialId,
          sortBy: currentSort,
          sortOrder: currentOrder,
          ...params,
        },
        { cancelToken }
      ),
    {
      pageSize,
      onError: (error) => {
        console.error('Error loading acts:', error);
      },
    }
  );

  const handleSortChange = useCallback((field: string) => {
    const nextOrder: 'asc' | 'desc' =
      field === currentSort && currentOrder === 'desc' ? 'asc' : 'desc';
    setCurrentSort(field);
    setCurrentOrder(nextOrder);
    // Pass the next values explicitly because component state
    // updates are async and the closure would still hold the old ones.
    refreshList({ sortBy: field, sortOrder: nextOrder });
  }, [currentSort, currentOrder, refreshList]);

  const handleActClick = useCallback((id: string) => {
    navigate(`/acts/${id}`);
  }, [navigate]);

  const handleOfficialClick = useCallback((id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/officials/${id}`);
  }, [navigate]);

  const renderActCard = useCallback((act: ActCard) => {
    const shittyPercent = act.totalVotes > 0
      ? (act.shittyVotes / act.totalVotes) * 100
      : 0;

    // Timestamps come from the API in epoch seconds; normalize to ms.
    const toMillis = (ts: number) => (ts > 1e12 ? ts : ts * 1000);
    const formattedDate = new Date(toMillis(act.timestamp)).toLocaleDateString();

    return (
      <div
        key={act.id}
        className="act-card card mb-3 cursor-pointer hover-shadow"
        onClick={() => handleActClick(act.id)}
      >
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h5 className="card-title mb-1">{act.title}</h5>
              {showOfficial && act.officialName && (
                <p
                  className="card-subtitle mb-2"
                  onClick={(e) => handleOfficialClick(act.officialId, e)}
                >
                  <span className="text-primary cursor-pointer">
                    {act.officialName}
                  </span>
                </p>
              )}
              <p className="card-text text-muted small mb-2">
                {act.description.substring(0, 200)}
                {act.description.length > 200 ? '...' : ''}
              </p>
              <div className="act-meta text-muted small">
                <span className="me-3">
                  <i className="bi bi-calendar me-1"></i>
                  {formattedDate}
                </span>
                <span className="me-3">
                  <i className="bi bi-tag me-1"></i>
                  {act.category}
                </span>
              </div>
            </div>
            <div className="text-end">
              <div className="h4 mb-0 text-danger">
                {shittyPercent.toFixed(1)}%
              </div>
              <small className="text-muted">
                {act.shittyVotes} / {act.totalVotes} votes
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }, [handleActClick, handleOfficialClick, showOfficial]);

  const renderSortButton = (field: string, label: string) => (
    <button
      className={`btn btn-sm ${currentSort === field ? 'btn-primary' : 'btn-outline-primary'}`}
      onClick={() => handleSortChange(field)}
    >
      {label}
      {currentSort === field && (
        <i className={`ms-1 bi bi-arrow-${currentOrder === 'asc' ? 'up' : 'down'}`} />
      )}
    </button>
  );

  return (
    <ErrorBoundary>
      <div className="acts-list">
        <div className="controls mb-4">
          <div className="btn-group">
            {renderSortButton('timestamp', 'Date')}
            {renderSortButton('shittyVotes', 'Shitty Votes')}
            {renderSortButton('totalVotes', 'Total Votes')}
          </div>
        </div>

        <LoadingState
          isLoading={isLoading}
          error={error}
          retry={refreshList}
          loadingMessage="Loading acts..."
          errorMessage="Error loading acts"
        >
          <div className="acts-grid">
            {acts?.map(renderActCard)}
            
            {isLoadingMore && (
              <div className="text-center my-3">
                <div className="spinner-border text-primary">
                  <span className="visually-hidden">Loading more...</span>
                </div>
              </div>
            )}

            {hasMore && !isLoadingMore && !error && (
              <div className="text-center my-3">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => loadMore()}
                >
                  Load More
                </button>
              </div>
            )}

            {!hasMore && acts?.length === 0 && (
              <div className="text-center text-muted my-5">
                <p>No acts found</p>
              </div>
            )}
          </div>
        </LoadingState>
      </div>
    </ErrorBoundary>
  );
};

export default ActsList;